<template>
  <div class="meissner-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$B_internal ≡ 0$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>超导状态 (Status)</label>
            <span class="val" :class="{ superconducting: isSC }">{{ isSC ? '超导态 (SC)' : '常导态 (Normal)' }}</span>
          </div>
          <div class="hud-item orange">
            <label>内部磁场 B_int</label>
            <span class="val">{{ isSC ? '0.00' : '0.85' }} <small>T</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240">
          <defs>
             <filter id="glow-sc"><feGaussianBlur stdDeviation="4" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <linearGradient id="sc-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" :stop-color="isSC ? '#34d399' : '#475569'" />
                <stop offset="100%" :stop-color="isSC ? '#059669' : '#1e293b'" />
             </linearGradient>
          </defs>

          <!-- 磁感线 (外部环境) -->
          <g transform="translate(200, 120)">
             <path v-for="i in 8" :key="'f'+i" 
                   :d="getFieldLine(i)" 
                   fill="none" 
                   :stroke="isSC ? '#facc15' : '#334155'" 
                   stroke-width="1.5" 
                   :opacity="isSC ? 0.8 : 0.4"
                   :stroke-dasharray="isSC ? '5,5' : 'none'">
                <animate v-if="isSC" attributeName="stroke-dashoffset" from="20" to="0" dur="0.8s" repeatCount="indefinite" />
             </path>
          </g>

          <!-- 超导体 (Superconductor) -->
          <g :transform="`translate(200, 170)`">
             <rect x="-60" y="-15" width="120" height="30" :fill="`url(#sc-grad)`" :stroke="isSC ? '#10b981' : '#64748b'" stroke-width="2" rx="4" :filter="isSC ? 'url(#glow-sc)' : ''" />
             <text y="5" text-anchor="middle" fill="#fff" font-size="10" font-weight="900" opacity="0.6">超导材料</text>
             
             <!-- 液氮冷却烟气 -->
             <g v-if="isSC">
                <circle v-for="n in 8" :key="n" :cx="-50 + n*12" :cy="-15 - (Math.random()*10)" r="3" fill="#fff" opacity="0.4">
                   <animate attributeName="cy" values="-15;-40" dur="1s" :begin="n*0.2 + 's'" repeatCount="indefinite" />
                   <animate attributeName="opacity" values="0.4;0" dur="1s" :begin="n*0.2 + 's'" repeatCount="indefinite" />
                </circle>
             </g>
          </g>

          <!-- 磁铁 (Floating Magnet) -->
          <g :transform="`translate(200, ${magnetY})`">
             <rect x="-25" y="-12" width="50" height="24" fill="#ef4444" rx="2" stroke="#fff" stroke-width="1" />
             <text x="-15" y="4" fill="#fff" font-size="10" font-weight="900">N</text>
             <rect x="0" y="-12" width="25" height="24" fill="#1e293b" rx="2" />
             <text x="10" y="4" fill="#fff" font-size="10" font-weight="900">S</text>
             
             <text y="28" text-anchor="middle" fill="#64748b" font-size="10" font-weight="bold">永磁体</text>
          </g>
        </svg>

        <div class="interaction-hint">切换材料状态观察完全抗磁性 (B=0) 排斥现象</div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex items-center justify-between">
              <span class="label">冷却至临界温度 Tc 以下</span>
              <div class="flex items-center gap-3">
                 <span class="status-indicator" :class="{ active: isSC }">{{ isSC ? '超导态' : '常导态' }}</span>
                 <el-switch v-model="isSC" active-color="#10b981" inactive-color="#475569"></el-switch>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>核心揭示：</strong>迈斯纳效应是超导体的二级相变。进入超导态后，材料会将内部原有的残余磁通量完全排出，使内部感应强度<strong>恒定为零</strong>。这种<strong>完全抗磁性</strong>产生的排斥力足以克服重力，实现稳定的磁悬浮。这与普通导体的涡流阻尼有着本质的热力学区别。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MeissnerEffectViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      isSC: true
    }
  },
  computed: {
    magnetY() {
       return this.isSC ? 75 : 125;
    }
  },
  methods: {
    getFieldLine(i) {
       const x = (i - 4.5) * 45;
       if (this.isSC) {
          // Lines bent AROUND the superconductor at Y=170
          // Source point is magnet at (200, 75)
          const bend = x > 0 ? 60 : -60;
          return `M ${x} ${this.magnetY - 120 + 12} Q ${x + bend} 10 ${x} 120`;
       } else {
          // Straight through
          return `M ${x} -100 L ${x} 120`;
       }
    }
  }
}
</script>

<style scoped lang="scss">
.meissner-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { 
        font-size: 14px; color: #94a3b8; font-weight: 800; font-family: monospace; 
        &.superconducting { color: #10b981; }
      }
      &.cyan { border-left: 3px solid #10b981; }
      &.orange { border-left: 3px solid #f97316; }
    }
  }
}

.interaction-hint { position: absolute; bottom: 10px; width: 100%; text-align: center; color: #334155; font-size: 10px; font-weight: bold; }

.status-indicator { font-size: 12px; font-weight: 900; color: #64748b; &.active { color: #10b981; } }

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .items-center { align-items: center; } .gap-3 { gap: 12px; } .justify-between { justify-content: space-between; }

.discovery-card-compact {
  background: #f0fdf4; padding: 10px 14px; border-radius: 10px; border: 1px solid #dcfce7; display: flex; align-items: center; gap: 10px;
  i { color: #10b981; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #166534; line-height: 1.5; strong { color: #14532d; } }
}
</style>
